import { type PartialCartItem } from '@cart-services/types'
import { toPartialCartItem } from '@cart-services/utils/to-partial-cart-item'
import { FAST_ORDER_ITEM_CHECK_STATUS, FAST_ORDER_REJECT_REASON, FAST_ORDER_ROW_ERROR_CODE, FAST_ORDER_ROW_STATUS, type FastOrderCsvValidationRow, type FastOrderFileError, type FastOrderItem, type FastOrderItemCheckRow, type FastOrderItemCheckStatusType, type FastOrderItemInput, type FastOrderRejectReasonType, type FastOrderRowErrorCodeType, type FastOrderSubmissionItem, type FastOrderSubmissionRejectedError } from '@hooks-api/types/dataModel/fast-order'
import { buildCsvItemKey, type CsvButlerItem, type CsvItemCheck } from '../../hooks/use-stm-quick-order-store'

export const csvRowErrorMessageMap: Record<FastOrderRowErrorCodeType, string> = {
  [FAST_ORDER_ROW_ERROR_CODE.BLANK_ROW]: '空白列',
  [FAST_ORDER_ROW_ERROR_CODE.MISSING_FIELD]: '欄位缺漏',
  [FAST_ORDER_ROW_ERROR_CODE.INVALID_QUANTITY]: '數量錯誤',
  [FAST_ORDER_ROW_ERROR_CODE.PRODUCT_NOT_FOUND]: '查無商品',
  [FAST_ORDER_ROW_ERROR_CODE.AMBIGUOUS_MATCH]: '無法唯一匹配商品',
  [FAST_ORDER_ROW_ERROR_CODE.ORDER_LIMIT_EXCEEDED]: '超過訂購上限',
  [FAST_ORDER_ROW_ERROR_CODE.BRAND_NOT_PERMITTED]: '無此品牌操作權限',
}

export const toCsvButlerItems = (items: FastOrderItem[]): CsvButlerItem[] =>
  items.map(item => ({
    key: buildCsvItemKey(item.project_code, item.real_sku),
    projectCode: item.project_code,
    realSku: item.real_sku,
    quantity: item.quantity,
    sourceRowNumbers: item.source_row_numbers,
    product: {
      sku: item.product.sku,
      variationId: item.product.variation_id,
      parentProductId: item.product.parent_product_id,
      parentProductName: item.product.parent_product_name,
      displayName: item.product.display_name,
      specName: item.product.spec_name ?? null,
      imageUrl: item.product.image_url ?? null,
      unit: item.product.unit,
      unitPrice: item.product.unit_price,
    },
    checked: true,
    isDeleted: false,
    check: null,
  }))

const toCsvRowErrorReason = (row: FastOrderCsvValidationRow) =>
  (row.error_code && csvRowErrorMessageMap[row.error_code]) || row.error_message || '資料有誤'

/** 只列錯誤列，依列號排序，一列一行給「檔案驗證結果」對話框 */
export const toCsvValidationErrorLines = (rows: FastOrderCsvValidationRow[]): string[] =>
  rows
    .filter(row => row.status === FAST_ORDER_ROW_STATUS.ERROR)
    .sort((left, right) => left.row_number - right.row_number)
    .map(row => `列號${row.row_number}，${row.brand_input}，${row.real_sku_input}，${row.quantity_input} 個，${toCsvRowErrorReason(row)}`)

export const toCsvFileErrorLine = (error: FastOrderFileError): string =>
  error.message || '檔案格式錯誤'

export const csvItemCheckStatusMessageMap: Record<Exclude<FastOrderItemCheckStatusType, typeof FAST_ORDER_ITEM_CHECK_STATUS.PASSED>, string> = {
  [FAST_ORDER_ITEM_CHECK_STATUS.OUT_OF_STOCK]: '缺貨',
  [FAST_ORDER_ITEM_CHECK_STATUS.INVALID_QUANTITY]: '數量不符合訂購限制',
  [FAST_ORDER_ITEM_CHECK_STATUS.STOCK_UNAVAILABLE]: '暫時無法取得庫存',
  [FAST_ORDER_ITEM_CHECK_STATUS.PRICING_UNAVAILABLE]: '暫時無法取得價格',
}

/** 後端回應裡沒有的品項用這個補上，走 blocked 呈現而不是永遠 pending */
export const unavailableCsvItemCheck: CsvItemCheck = {
  status: FAST_ORDER_ITEM_CHECK_STATUS.STOCK_UNAVAILABLE,
  remainingQuantity: null,
  unitPrice: null,
  amount: null,
  errorMessage: csvItemCheckStatusMessageMap[FAST_ORDER_ITEM_CHECK_STATUS.STOCK_UNAVAILABLE],
}

export const toFastOrderItemInputs = (items: CsvButlerItem[]): FastOrderItemInput[] =>
  items.map(item => ({ project_code: item.projectCode, real_sku: item.realSku, quantity: item.quantity }))

export const toCsvItemChecks = (rows: FastOrderItemCheckRow[]): Record<CsvButlerItem['key'], CsvItemCheck> =>
  Object.fromEntries(rows.map(row => [
    buildCsvItemKey(row.project_code, row.real_sku),
    {
      status: row.status,
      remainingQuantity: row.remaining_quantity,
      unitPrice: row.unit_price ?? null,
      amount: row.amount ?? null,
      errorMessage: row.error_message ?? (row.status === FAST_ORDER_ITEM_CHECK_STATUS.PASSED ? null : csvItemCheckStatusMessageMap[row.status]),
    },
  ]))

export const toFastOrderSubmissionItems = (items: CsvButlerItem[]): FastOrderSubmissionItem[] =>
  items.map(item => ({ project_code: item.projectCode, real_sku: item.realSku, quantity: item.quantity, source_row_numbers: item.sourceRowNumbers }))

export const csvRejectReasonMessageMap: Record<FastOrderRejectReasonType, string> = {
  [FAST_ORDER_REJECT_REASON.OUT_OF_STOCK]: '庫存不足',
  [FAST_ORDER_REJECT_REASON.ORDER_LIMIT_EXCEEDED]: '超過訂購上限',
  [FAST_ORDER_REJECT_REASON.PRODUCT_INACTIVE]: '商品已下架',
  [FAST_ORDER_REJECT_REASON.OWNERSHIP_CHANGED]: '商品歸屬已變更',
  [FAST_ORDER_REJECT_REASON.IDENTITY_INVALID]: '登入狀態已失效',
  [FAST_ORDER_REJECT_REASON.DATA_UNAVAILABLE]: '暫時無法取得商品資料',
}

const rejectReasonToCheckStatus = (reason: FastOrderRejectReasonType): FastOrderItemCheckStatusType => {
  if (reason === FAST_ORDER_REJECT_REASON.OUT_OF_STOCK || reason === FAST_ORDER_REJECT_REASON.ORDER_LIMIT_EXCEEDED) return FAST_ORDER_ITEM_CHECK_STATUS.OUT_OF_STOCK
  return FAST_ORDER_ITEM_CHECK_STATUS.STOCK_UNAVAILABLE
}

/** 提交被退回時把每筆錯誤套回對應品項，畫面沿用 items-check 的未通過呈現 */
export const toCsvItemChecksFromRejections = (errors: FastOrderSubmissionRejectedError[]): Record<CsvButlerItem['key'], CsvItemCheck> =>
  Object.fromEntries(errors.map(error => [
    buildCsvItemKey(error.project_code, error.real_sku),
    {
      status: rejectReasonToCheckStatus(error.reason),
      remainingQuantity: error.latest_remaining_quantity ?? null,
      unitPrice: null,
      amount: null,
      errorMessage: csvRejectReasonMessageMap[error.reason],
    },
  ]))

export const toStmCartItems = (items: CsvButlerItem[]): PartialCartItem[] =>
  items.flatMap((item) => {
    const cartItem = toPartialCartItem({
      variation_id: item.product.variationId,
      parent_product_id: item.product.parentProductId,
      sku: item.product.sku,
      project_code: item.projectCode,
      quantity: item.quantity,
    })
    return cartItem ? [cartItem] : []
  })
